'use client';
import React from 'react';

const pillars = [
  {
    number: '01',
    title: 'Institutional Process',
    desc: 'We apply the same rigorous investment process used by major pension funds and endowments — bringing institutional discipline to independent advisory practices.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Curated Manager Selection',
    desc: 'Access to a carefully curated universe of top-tier alternative managers across private credit, hedge funds, real assets, and private equity.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Manager Due Diligence',
    desc: 'Comprehensive operational, quantitative, and qualitative due diligence on every manager — protecting your clients and your practice.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Portfolio Integration',
    desc: 'Seamless integration of alternative allocations into existing client portfolios with sophisticated modeling, risk analysis, and ongoing rebalancing.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Simplifying Complexity',
    desc: 'We translate complex alternative investment strategies into clear, client-friendly narratives — making it easy to communicate value to your clients.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
  {
    number: '06',
    title: 'Fiduciary Standards',
    desc: 'We operate under the highest fiduciary standards, ensuring every recommendation is made in the best interest of your clients — always.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    number: '07',
    title: 'Proven Value of Alternatives',
    desc: 'Decades of institutional data demonstrate that properly allocated alternatives enhance portfolio outcomes — we bring that evidence to your practice.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    number: '08',
    title: 'Operational Efficiency',
    desc: 'Streamlined subscription, reporting, and compliance workflows reduce the operational burden of alternatives — freeing you to focus on client relationships.',
    icon: (
      <svg width="28" height="28" fill="none" stroke="#4060a1" strokeWidth="1.5" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
  },
];

export default function WhatWeDo() {
  return (
    <section id="what-we-do" className="bg-white">
      {/* Intro */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-0.5" style={{ backgroundColor: '#c9a84c', height: '2px' }} />
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#c9a84c', letterSpacing: '0.2em' }}>
            What We Do
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-black mb-4" style={{ color: '#1a2744', fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.15', fontFamily: 'Lato, sans-serif' }}>
              Institutional Pedigree.
              <span className="block" style={{ color: '#4060a1' }}>Advisor-Centered Design.</span>
            </h2>
          </div>
          <div>
            <p className="text-base leading-relaxed" style={{ color: '#666666' }}>
              Timber Point Capital Management bridges the gap between institutional alternative investment management and independent financial advisors — delivering the depth, discipline, and resources of a major institution without the constraints. Powered by Fortis Capital Advisors.
            </p>
          </div>
        </div>

        {/* 8 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {pillars?.map((pillar, i) => (
            <div
              key={i}
              className="group p-6 border border-[#e0e4ec] bg-[#f5f6f8] hover:border-[#4060a1] hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              {/* Number */}
              <div
                className="text-xs font-bold mb-4 tracking-wider"
                style={{ color: '#c9a84c', letterSpacing: '0.15em' }}
              >
                {pillar?.number}
              </div>
              {/* Icon */}
              <div className="mb-4">{pillar?.icon}</div>
              {/* Title */}
              <h3
                className="font-bold mb-3 text-base leading-snug group-hover:text-[#4060a1] transition-colors"
                style={{ color: '#1a2744' }}
              >
                {pillar?.title}
              </h3>
              {/* Description */}
              <p className="text-sm leading-relaxed" style={{ color: '#666666' }}>
                {pillar?.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
